import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useSequenceTemplates } from "@/hooks/use-shared-queries";
import type { SequenceTemplate } from "@shared/schema";
import { compareSequenceIds } from "@/lib/sequence-utils";

interface SequenceConfigContextValue {
  sequenceTemplates: SequenceTemplate[];
  sequenceColors: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
}

const SequenceConfigContext = createContext<SequenceConfigContextValue | undefined>(undefined);

interface SequenceConfigProviderProps {
  children: ReactNode;
}

export function SequenceConfigProvider({ children }: SequenceConfigProviderProps) {
  // Use shared query hook to prevent duplicate fetches
  const { data: rawSequenceTemplates = [] as SequenceTemplate[], isLoading, error } = useSequenceTemplates();

  // Sort templates: MST first, then numeric (01-99), then alpha (A-Z)
  const sequenceTemplates = useMemo(() => {
    return [...rawSequenceTemplates].sort((a, b) => 
      compareSequenceIds(a.sequenceNumber, b.sequenceNumber)
    );
  }, [rawSequenceTemplates]);

  // Pre-compute color lookup for performance
  const sequenceColors = React.useMemo(() => {
    const colors: Record<string, string> = {};
    sequenceTemplates.forEach((template: SequenceTemplate) => {
      colors[template.sequenceNumber] = template.defaultColor;
    });
    return colors;
  }, [sequenceTemplates]);

  const value = {
    sequenceTemplates,
    sequenceColors,
    isLoading,
    error: error as Error | null,
  };

  return (
    <SequenceConfigContext.Provider value={value}>
      {children}
    </SequenceConfigContext.Provider>
  );
}

export function useSequenceConfig() {
  const context = useContext(SequenceConfigContext);
  if (context === undefined) {
    throw new Error("useSequenceConfig must be used within a SequenceConfigProvider");
  }
  return context;
}

// Hook to get color for a specific sequence identifier
export function useSequenceColor(sequenceId: string): string {
  const { sequenceColors } = useSequenceConfig();
  return sequenceColors[sequenceId] || '#3B82F6';
}